/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  DeleteTableAccessParams,
  ListTableAccessParams,
  TLBulkActionResponseFoundryTableAccessBulkResult,
  TLFoundryTableAccessBulkListRequest,
  TLFoundryTableAccessBulkListResponse,
  TLFoundryTableAccessBulkRevokeRequest,
  TLFoundryTableAccessBulkSetRequest,
  TLFoundryTableAccessCapabilitiesResponse,
  TLFoundryTableAccessEntryResponse,
  TLFoundryTableAccessListResponse,
  TLFoundryTableAccessSetRequest,
  TLHTTPValidationError,
  UpdateTableAccessParams,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Delete Table Access
 */
export const deleteTableAccess = (
  params: DeleteTableAccessParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<void>({ url: `/foundry/table-access`, method: 'DELETE', params, signal }, options);
};

export const getDeleteTableAccessQueryKey = (params?: DeleteTableAccessParams) => {
  return ['DELETE', 'foundry', 'table-access', ...(params ? [params] : [])] as const;
};

export const getDeleteTableAccessQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: DeleteTableAccessParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteTableAccess>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteTableAccessQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteTableAccess>>> = ({ signal }) =>
    deleteTableAccess(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof deleteTableAccess>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type DeleteTableAccessQueryResult = NonNullable<Awaited<ReturnType<typeof deleteTableAccess>>>;
export type DeleteTableAccessQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteTableAccess<
  TData = Awaited<ReturnType<typeof deleteTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: DeleteTableAccessParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteTableAccess>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteTableAccess>>,
          TError,
          Awaited<ReturnType<typeof deleteTableAccess>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteTableAccess<
  TData = Awaited<ReturnType<typeof deleteTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: DeleteTableAccessParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteTableAccess>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteTableAccess>>,
          TError,
          Awaited<ReturnType<typeof deleteTableAccess>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteTableAccess<
  TData = Awaited<ReturnType<typeof deleteTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: DeleteTableAccessParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteTableAccess>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Table Access
 */

export function useDeleteTableAccess<
  TData = Awaited<ReturnType<typeof deleteTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: DeleteTableAccessParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteTableAccess>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteTableAccessQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Table Access
 */
export const prefetchDeleteTableAccessQuery = async <
  TData = Awaited<ReturnType<typeof deleteTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params: DeleteTableAccessParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteTableAccess>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteTableAccessQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Table Access
 */
export const invalidateDeleteTableAccess = async (
  queryClient: QueryClient,
  params: DeleteTableAccessParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteTableAccessQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary List Table Access
 */
export const listTableAccess = (
  params?: ListTableAccessParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLFoundryTableAccessListResponse>(
    { url: `/foundry/table-access`, method: 'GET', params, signal },
    options,
  );
};

export const getListTableAccessQueryKey = (params?: ListTableAccessParams) => {
  return ['foundry', 'table-access', ...(params ? [params] : [])] as const;
};

export const getListTableAccessQueryOptions = <
  TData = Awaited<ReturnType<typeof listTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListTableAccessParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listTableAccess>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListTableAccessQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listTableAccess>>> = ({ signal }) =>
    listTableAccess(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listTableAccess>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListTableAccessQueryResult = NonNullable<Awaited<ReturnType<typeof listTableAccess>>>;
export type ListTableAccessQueryError = ErrorType<TLHTTPValidationError>;

export function useListTableAccess<
  TData = Awaited<ReturnType<typeof listTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | ListTableAccessParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listTableAccess>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTableAccess>>,
          TError,
          Awaited<ReturnType<typeof listTableAccess>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListTableAccess<
  TData = Awaited<ReturnType<typeof listTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListTableAccessParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listTableAccess>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTableAccess>>,
          TError,
          Awaited<ReturnType<typeof listTableAccess>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListTableAccess<
  TData = Awaited<ReturnType<typeof listTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListTableAccessParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listTableAccess>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Table Access
 */

export function useListTableAccess<
  TData = Awaited<ReturnType<typeof listTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListTableAccessParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listTableAccess>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListTableAccessQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Table Access
 */
export const prefetchListTableAccessQuery = async <
  TData = Awaited<ReturnType<typeof listTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: ListTableAccessParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listTableAccess>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListTableAccessQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Table Access
 */
export const invalidateListTableAccess = async (
  queryClient: QueryClient,
  params?: ListTableAccessParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListTableAccessQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Update Table Access
 */
export const updateTableAccess = (
  tLFoundryTableAccessSetRequest: BodyType<TLFoundryTableAccessSetRequest>,
  params: UpdateTableAccessParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLFoundryTableAccessEntryResponse>(
    {
      url: `/foundry/table-access`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: tLFoundryTableAccessSetRequest,
      params,
      signal,
    },
    options,
  );
};

export const getUpdateTableAccessQueryKey = (
  tLFoundryTableAccessSetRequest?: BodyType<TLFoundryTableAccessSetRequest>,
  params?: UpdateTableAccessParams,
) => {
  return ['PUT', 'foundry', 'table-access', ...(params ? [params] : []), tLFoundryTableAccessSetRequest] as const;
};

export const getUpdateTableAccessQueryOptions = <
  TData = Awaited<ReturnType<typeof updateTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLFoundryTableAccessSetRequest: BodyType<TLFoundryTableAccessSetRequest>,
  params: UpdateTableAccessParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateTableAccess>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUpdateTableAccessQueryKey(tLFoundryTableAccessSetRequest, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateTableAccess>>> = ({ signal }) =>
    updateTableAccess(tLFoundryTableAccessSetRequest, params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof updateTableAccess>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type UpdateTableAccessQueryResult = NonNullable<Awaited<ReturnType<typeof updateTableAccess>>>;
export type UpdateTableAccessQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateTableAccess<
  TData = Awaited<ReturnType<typeof updateTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLFoundryTableAccessSetRequest: BodyType<TLFoundryTableAccessSetRequest>,
  params: UpdateTableAccessParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateTableAccess>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateTableAccess>>,
          TError,
          Awaited<ReturnType<typeof updateTableAccess>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateTableAccess<
  TData = Awaited<ReturnType<typeof updateTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLFoundryTableAccessSetRequest: BodyType<TLFoundryTableAccessSetRequest>,
  params: UpdateTableAccessParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateTableAccess>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateTableAccess>>,
          TError,
          Awaited<ReturnType<typeof updateTableAccess>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateTableAccess<
  TData = Awaited<ReturnType<typeof updateTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLFoundryTableAccessSetRequest: BodyType<TLFoundryTableAccessSetRequest>,
  params: UpdateTableAccessParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateTableAccess>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Table Access
 */

export function useUpdateTableAccess<
  TData = Awaited<ReturnType<typeof updateTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLFoundryTableAccessSetRequest: BodyType<TLFoundryTableAccessSetRequest>,
  params: UpdateTableAccessParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateTableAccess>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateTableAccessQueryOptions(tLFoundryTableAccessSetRequest, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Table Access
 */
export const prefetchUpdateTableAccessQuery = async <
  TData = Awaited<ReturnType<typeof updateTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLFoundryTableAccessSetRequest: BodyType<TLFoundryTableAccessSetRequest>,
  params: UpdateTableAccessParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateTableAccess>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateTableAccessQueryOptions(tLFoundryTableAccessSetRequest, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Table Access
 */
export const invalidateUpdateTableAccess = async (
  queryClient: QueryClient,
  tLFoundryTableAccessSetRequest: BodyType<TLFoundryTableAccessSetRequest>,
  params: UpdateTableAccessParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateTableAccessQueryKey(tLFoundryTableAccessSetRequest, params) },
    options,
  );

  return queryClient;
};

/**
 * Read-only: who can use the given nodes (direct and inherited), resolved from one walk of the tenant's grants; each entry is listed once with the nodes it covers. POST because the target list does not fit a query string.
 * @summary Bulk List Table Access
 */
export const bulkListTableAccess = (
  tLFoundryTableAccessBulkListRequest: BodyType<TLFoundryTableAccessBulkListRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLFoundryTableAccessBulkListResponse>(
    {
      url: `/foundry/table-access/bulk-list`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLFoundryTableAccessBulkListRequest,
      signal,
    },
    options,
  );
};

export const getBulkListTableAccessQueryKey = (
  tLFoundryTableAccessBulkListRequest?: BodyType<TLFoundryTableAccessBulkListRequest>,
) => {
  return ['POST', 'foundry', 'table-access', 'bulk-list', tLFoundryTableAccessBulkListRequest] as const;
};

export const getBulkListTableAccessQueryOptions = <
  TData = Awaited<ReturnType<typeof bulkListTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLFoundryTableAccessBulkListRequest: BodyType<TLFoundryTableAccessBulkListRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkListTableAccess>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getBulkListTableAccessQueryKey(tLFoundryTableAccessBulkListRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof bulkListTableAccess>>> = ({ signal }) =>
    bulkListTableAccess(tLFoundryTableAccessBulkListRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof bulkListTableAccess>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type BulkListTableAccessQueryResult = NonNullable<Awaited<ReturnType<typeof bulkListTableAccess>>>;
export type BulkListTableAccessQueryError = ErrorType<TLHTTPValidationError>;

export function useBulkListTableAccess<
  TData = Awaited<ReturnType<typeof bulkListTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLFoundryTableAccessBulkListRequest: BodyType<TLFoundryTableAccessBulkListRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkListTableAccess>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkListTableAccess>>,
          TError,
          Awaited<ReturnType<typeof bulkListTableAccess>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkListTableAccess<
  TData = Awaited<ReturnType<typeof bulkListTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLFoundryTableAccessBulkListRequest: BodyType<TLFoundryTableAccessBulkListRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkListTableAccess>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkListTableAccess>>,
          TError,
          Awaited<ReturnType<typeof bulkListTableAccess>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkListTableAccess<
  TData = Awaited<ReturnType<typeof bulkListTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLFoundryTableAccessBulkListRequest: BodyType<TLFoundryTableAccessBulkListRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkListTableAccess>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Bulk List Table Access
 */

export function useBulkListTableAccess<
  TData = Awaited<ReturnType<typeof bulkListTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLFoundryTableAccessBulkListRequest: BodyType<TLFoundryTableAccessBulkListRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkListTableAccess>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getBulkListTableAccessQueryOptions(tLFoundryTableAccessBulkListRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Bulk List Table Access
 */
export const prefetchBulkListTableAccessQuery = async <
  TData = Awaited<ReturnType<typeof bulkListTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLFoundryTableAccessBulkListRequest: BodyType<TLFoundryTableAccessBulkListRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkListTableAccess>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getBulkListTableAccessQueryOptions(tLFoundryTableAccessBulkListRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Bulk List Table Access
 */
export const invalidateBulkListTableAccess = async (
  queryClient: QueryClient,
  tLFoundryTableAccessBulkListRequest: BodyType<TLFoundryTableAccessBulkListRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getBulkListTableAccessQueryKey(tLFoundryTableAccessBulkListRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Bulk Revoke Table Access
 */
export const bulkRevokeTableAccess = (
  tLFoundryTableAccessBulkRevokeRequest: BodyType<TLFoundryTableAccessBulkRevokeRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLBulkActionResponseFoundryTableAccessBulkResult>(
    {
      url: `/foundry/table-access/bulk-revoke`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLFoundryTableAccessBulkRevokeRequest,
      signal,
    },
    options,
  );
};

export const getBulkRevokeTableAccessQueryKey = (
  tLFoundryTableAccessBulkRevokeRequest?: BodyType<TLFoundryTableAccessBulkRevokeRequest>,
) => {
  return ['POST', 'foundry', 'table-access', 'bulk-revoke', tLFoundryTableAccessBulkRevokeRequest] as const;
};

export const getBulkRevokeTableAccessQueryOptions = <
  TData = Awaited<ReturnType<typeof bulkRevokeTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLFoundryTableAccessBulkRevokeRequest: BodyType<TLFoundryTableAccessBulkRevokeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkRevokeTableAccess>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getBulkRevokeTableAccessQueryKey(tLFoundryTableAccessBulkRevokeRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof bulkRevokeTableAccess>>> = ({ signal }) =>
    bulkRevokeTableAccess(tLFoundryTableAccessBulkRevokeRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof bulkRevokeTableAccess>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type BulkRevokeTableAccessQueryResult = NonNullable<Awaited<ReturnType<typeof bulkRevokeTableAccess>>>;
export type BulkRevokeTableAccessQueryError = ErrorType<TLHTTPValidationError>;

export function useBulkRevokeTableAccess<
  TData = Awaited<ReturnType<typeof bulkRevokeTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLFoundryTableAccessBulkRevokeRequest: BodyType<TLFoundryTableAccessBulkRevokeRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkRevokeTableAccess>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkRevokeTableAccess>>,
          TError,
          Awaited<ReturnType<typeof bulkRevokeTableAccess>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkRevokeTableAccess<
  TData = Awaited<ReturnType<typeof bulkRevokeTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLFoundryTableAccessBulkRevokeRequest: BodyType<TLFoundryTableAccessBulkRevokeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkRevokeTableAccess>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkRevokeTableAccess>>,
          TError,
          Awaited<ReturnType<typeof bulkRevokeTableAccess>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkRevokeTableAccess<
  TData = Awaited<ReturnType<typeof bulkRevokeTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLFoundryTableAccessBulkRevokeRequest: BodyType<TLFoundryTableAccessBulkRevokeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkRevokeTableAccess>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Bulk Revoke Table Access
 */

export function useBulkRevokeTableAccess<
  TData = Awaited<ReturnType<typeof bulkRevokeTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLFoundryTableAccessBulkRevokeRequest: BodyType<TLFoundryTableAccessBulkRevokeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkRevokeTableAccess>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getBulkRevokeTableAccessQueryOptions(tLFoundryTableAccessBulkRevokeRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Bulk Revoke Table Access
 */
export const prefetchBulkRevokeTableAccessQuery = async <
  TData = Awaited<ReturnType<typeof bulkRevokeTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLFoundryTableAccessBulkRevokeRequest: BodyType<TLFoundryTableAccessBulkRevokeRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkRevokeTableAccess>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getBulkRevokeTableAccessQueryOptions(tLFoundryTableAccessBulkRevokeRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Bulk Revoke Table Access
 */
export const invalidateBulkRevokeTableAccess = async (
  queryClient: QueryClient,
  tLFoundryTableAccessBulkRevokeRequest: BodyType<TLFoundryTableAccessBulkRevokeRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getBulkRevokeTableAccessQueryKey(tLFoundryTableAccessBulkRevokeRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Bulk Set Table Access
 */
export const bulkSetTableAccess = (
  tLFoundryTableAccessBulkSetRequest: BodyType<TLFoundryTableAccessBulkSetRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLBulkActionResponseFoundryTableAccessBulkResult>(
    {
      url: `/foundry/table-access/bulk-set`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLFoundryTableAccessBulkSetRequest,
      signal,
    },
    options,
  );
};

export const getBulkSetTableAccessQueryKey = (
  tLFoundryTableAccessBulkSetRequest?: BodyType<TLFoundryTableAccessBulkSetRequest>,
) => {
  return ['POST', 'foundry', 'table-access', 'bulk-set', tLFoundryTableAccessBulkSetRequest] as const;
};

export const getBulkSetTableAccessQueryOptions = <
  TData = Awaited<ReturnType<typeof bulkSetTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLFoundryTableAccessBulkSetRequest: BodyType<TLFoundryTableAccessBulkSetRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkSetTableAccess>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getBulkSetTableAccessQueryKey(tLFoundryTableAccessBulkSetRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof bulkSetTableAccess>>> = ({ signal }) =>
    bulkSetTableAccess(tLFoundryTableAccessBulkSetRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof bulkSetTableAccess>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type BulkSetTableAccessQueryResult = NonNullable<Awaited<ReturnType<typeof bulkSetTableAccess>>>;
export type BulkSetTableAccessQueryError = ErrorType<TLHTTPValidationError>;

export function useBulkSetTableAccess<
  TData = Awaited<ReturnType<typeof bulkSetTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLFoundryTableAccessBulkSetRequest: BodyType<TLFoundryTableAccessBulkSetRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkSetTableAccess>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkSetTableAccess>>,
          TError,
          Awaited<ReturnType<typeof bulkSetTableAccess>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkSetTableAccess<
  TData = Awaited<ReturnType<typeof bulkSetTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLFoundryTableAccessBulkSetRequest: BodyType<TLFoundryTableAccessBulkSetRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkSetTableAccess>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkSetTableAccess>>,
          TError,
          Awaited<ReturnType<typeof bulkSetTableAccess>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkSetTableAccess<
  TData = Awaited<ReturnType<typeof bulkSetTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLFoundryTableAccessBulkSetRequest: BodyType<TLFoundryTableAccessBulkSetRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkSetTableAccess>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Bulk Set Table Access
 */

export function useBulkSetTableAccess<
  TData = Awaited<ReturnType<typeof bulkSetTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLFoundryTableAccessBulkSetRequest: BodyType<TLFoundryTableAccessBulkSetRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkSetTableAccess>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getBulkSetTableAccessQueryOptions(tLFoundryTableAccessBulkSetRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Bulk Set Table Access
 */
export const prefetchBulkSetTableAccessQuery = async <
  TData = Awaited<ReturnType<typeof bulkSetTableAccess>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLFoundryTableAccessBulkSetRequest: BodyType<TLFoundryTableAccessBulkSetRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkSetTableAccess>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getBulkSetTableAccessQueryOptions(tLFoundryTableAccessBulkSetRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Bulk Set Table Access
 */
export const invalidateBulkSetTableAccess = async (
  queryClient: QueryClient,
  tLFoundryTableAccessBulkSetRequest: BodyType<TLFoundryTableAccessBulkSetRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getBulkSetTableAccessQueryKey(tLFoundryTableAccessBulkSetRequest) },
    options,
  );

  return queryClient;
};

/**
 * What the caller may do with table access: whether the access dialogs open for them at all (foundry-admin, or any Data Foundry user once the manage guard is enforced) and whether they may search people and groups to grant to others (foundry-admin, or a manager somewhere). Per-node rights come back on the lists.
 * @summary Get Table Access Capabilities
 */
export const getTableAccessCapabilities = (options?: SecondParameter<typeof apiInstance>, signal?: AbortSignal) => {
  return apiInstance<TLFoundryTableAccessCapabilitiesResponse>(
    { url: `/foundry/table-access/me`, method: 'GET', signal },
    options,
  );
};

export const getGetTableAccessCapabilitiesQueryKey = () => {
  return ['foundry', 'table-access', 'me'] as const;
};

export const getGetTableAccessCapabilitiesQueryOptions = <
  TData = Awaited<ReturnType<typeof getTableAccessCapabilities>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTableAccessCapabilities>>, TError, TData>>;
  request?: SecondParameter<typeof apiInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTableAccessCapabilitiesQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getTableAccessCapabilities>>> = ({ signal }) =>
    getTableAccessCapabilities(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getTableAccessCapabilities>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetTableAccessCapabilitiesQueryResult = NonNullable<Awaited<ReturnType<typeof getTableAccessCapabilities>>>;
export type GetTableAccessCapabilitiesQueryError = ErrorType<unknown>;

export function useGetTableAccessCapabilities<
  TData = Awaited<ReturnType<typeof getTableAccessCapabilities>>,
  TError = ErrorType<unknown>,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTableAccessCapabilities>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTableAccessCapabilities>>,
          TError,
          Awaited<ReturnType<typeof getTableAccessCapabilities>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetTableAccessCapabilities<
  TData = Awaited<ReturnType<typeof getTableAccessCapabilities>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTableAccessCapabilities>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTableAccessCapabilities>>,
          TError,
          Awaited<ReturnType<typeof getTableAccessCapabilities>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetTableAccessCapabilities<
  TData = Awaited<ReturnType<typeof getTableAccessCapabilities>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTableAccessCapabilities>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Table Access Capabilities
 */

export function useGetTableAccessCapabilities<
  TData = Awaited<ReturnType<typeof getTableAccessCapabilities>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTableAccessCapabilities>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetTableAccessCapabilitiesQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Table Access Capabilities
 */
export const prefetchGetTableAccessCapabilitiesQuery = async <
  TData = Awaited<ReturnType<typeof getTableAccessCapabilities>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTableAccessCapabilities>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetTableAccessCapabilitiesQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Table Access Capabilities
 */
export const invalidateGetTableAccessCapabilities = async (
  queryClient: QueryClient,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetTableAccessCapabilitiesQueryKey() }, options);

  return queryClient;
};
